/**
 * Navigation utility functions for the Creative system
 * Consolidates common logic used across navigation hooks
 */

import { CREATIVE_ROUTE_CONFIG } from '../config/routeConfig';

/**
 * Safely parse URL search parameters
 */
export const getURLParam = (search, key, defaultValue = '') => {
  try {
    return new URLSearchParams(search).get(key) || defaultValue;
  } catch {
    return defaultValue;
  }
};

/**
 * Parse pathname into clean segments
 */
export const parsePathParts = (pathname) => {
  return pathname.split('/').filter(Boolean);
};

/**
 * Extract page number from pathname
 */
export const getPageFromPath = (pathname) => {
  const parts = parsePathParts(pathname);
  const lastPart = parts[parts.length - 1];
  return /^\d+$/.test(lastPart) ? parseInt(lastPart, 10) : 1;
};

/**
 * Get default subtab for a given tab
 */
export const getDefaultSubtab = (tab) => {
  const validSubtabs = CREATIVE_ROUTE_CONFIG.validSubtabs[tab];
  return validSubtabs?.[0] || 'displayAdsUnits';
};

/**
 * Validate individual route parameters
 */
export const validateRouteParams = (tab, subtab, pageNumber) => {
  const isValidTab = CREATIVE_ROUTE_CONFIG.validTabs.includes(tab);
  const isValidSubtab = CREATIVE_ROUTE_CONFIG.validSubtabs[tab]?.includes(subtab);
  const isValidPage = pageNumber > 0;

  return {
    isValidTab,
    isValidSubtab,
    isValidPage,
    isValid: isValidTab && isValidSubtab && isValidPage
  };
};

/**
 * Get detail paths using RESTful pattern
 * @param {string} entityType - 'unit' or 'group'
 * @param {string} mediaType - 'DISPLAY' or 'VIDEO'
 * @returns {string} RESTful path
 */
export const getDetailPath = (entityType, mediaType = 'DISPLAY') => {
  const mediaTypePath = mediaType === 'VIDEO' ? 'video' : 'display';
  const entityTypePath = entityType.toLowerCase() === 'adunit' || entityType.toLowerCase() === 'unit' ? 'units' : 'groups';

  return `/creatives/${mediaTypePath}/${entityTypePath}`;
};

/**
 * Legacy function for backward compatibility
 * @deprecated Use getDetailPath with new parameters
 */
export const getLegacyDetailPath = (detailType, mediaType = 'DISPLAY') => {
  const legacyPaths = {
    adunit: mediaType === 'VIDEO' ? '/creatives/VideoDetails' : '/creatives/AdUnit',
    adgroup: mediaType === 'VIDEO' ? '/creatives/VideoGroupDetail' : '/creatives/AdGroupDetail'
  };

  return legacyPaths[detailType.toLowerCase()];
};

/**
 * Build URL for navigation using RESTful pattern
 */
export const buildCreativesURL = (tab, subtab, page) => {
  const mediaType = tab === 'videoAds' ? 'video' : 'display';
  const entityType = subtab.includes('Group') || subtab === 'adGroups' || subtab === 'adVideoGroups' ? 'groups' : 'units';

  const baseUrl = `/creatives/${mediaType}/${entityType}`;
  return page > 1 ? `${baseUrl}?page=${page}` : baseUrl;
};

/**
 * Legacy URL builder for backward compatibility
 * @deprecated Use buildCreativesURL with RESTful pattern
 */
export const buildLegacyCreativesURL = (tab, subtab, page) => {
  const searchParams = new URLSearchParams();
  searchParams.set('tab', tab);
  searchParams.set('subtab', subtab);
  return `/creatives/main/${page}?${searchParams.toString()}`;
};

/**
 * Get navigation helpers for current context
 */
export const getNavigationHelpers = (tab, subtab) => {
  return {
    isDisplayAds: tab === 'displayAds',
    isVideoAds: tab === 'videoAds',
    isAdUnits: subtab.includes('Units') || subtab === 'videos',
    isAdGroups: subtab.includes('Groups') || subtab.includes('adGroups'),
    mediaType: tab === 'videoAds' ? 'VIDEO' : 'DISPLAY'
  };
};